import { useTranslation } from "react-i18next";

import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { useActiveTabFromMatch } from "../../../hooks/useActiveTabFromMatch";
import { TabSideNavigation } from "../../../tab-side-navigation/TabSideNavigation";
import { TabSideNavigationItem } from "../../../tab-side-navigation/TabSideNavigation.types";
import { AssetInventoryCompletenessSection } from "../../api/ClientFileApi";
import { useOptionalDocumentDndApi } from "../../api/ClientFileApiContext";
import { useClientFileContext } from "../../context/ClientFileContext";
import { AssetInventorySection as AssetInventorySubTab } from "../../routes/clientFileRoutes";
import { useAssetInventoryCompleteness } from "../hooks/useAssetInventoryCompleteness";

const SectionParamName = "section";

const COMPLETENESS_SECTIONS: Partial<Record<string, AssetInventoryCompletenessSection>> = {
  [AssetInventorySubTab.Relatives]: AssetInventoryCompletenessSection.Relatives,
  [AssetInventorySubTab.Income]: AssetInventoryCompletenessSection.Income,
  [AssetInventorySubTab.Expenses]: AssetInventoryCompletenessSection.Expenses,
  [AssetInventorySubTab.OwnClaims]: AssetInventoryCompletenessSection.OwnClaims,
  [AssetInventorySubTab.Assets]: AssetInventoryCompletenessSection.Assets,
  [AssetInventorySubTab.OtherPropertyRights]: AssetInventoryCompletenessSection.PropertyRights,
  [AssetInventorySubTab.Donations]: AssetInventoryCompletenessSection.Donations,
};

export function AssetInventoryNavigation() {
  const { t } = useTranslation();
  const { clientId, basePaths } = useClientFileContext();
  // The documents overview is an internal-app view (bulk actions on the app's document zones), so
  // its entry only shows where the documentDnd slice is provided.
  const hasDocumentsOverview = useOptionalDocumentDndApi() !== undefined;
  const activeSubTab = useActiveTabFromMatch(
    `${basePaths.assetInventory}/:${SectionParamName}`,
    AssetInventorySubTab,
    SectionParamName
  );

  const { data: completeness } = useAssetInventoryCompleteness(clientId);

  const hasWarning = (subTab: string) =>
    completeness?.sections.some((s) => s.section === COMPLETENESS_SECTIONS[subTab] && s.hasWarning) ?? false;

  const withWarning = (item: TabSideNavigationItem): TabSideNavigationItem =>
    hasWarning(item.key)
      ? {
          ...item,
          endAdornment: (
            <WarningAmberIcon
              fontSize="small"
              color="warning"
              titleAccess={t("clients.client.debtSettlement.assetInventory.sectionIncomplete")}
            />
          ),
        }
      : item;

  const items: TabSideNavigationItem[] = [
    {
      key: AssetInventorySubTab.PersonalData,
      label: t("clients.client.debtSettlement.assetInventory.personalData"),
      to: `${basePaths.assetInventory}/${AssetInventorySubTab.PersonalData}`,
    },
    {
      key: AssetInventorySubTab.Relatives,
      label: t("clients.client.debtSettlement.assetInventory.relatives"),
      to: `${basePaths.assetInventory}/${AssetInventorySubTab.Relatives}`,
    },
    {
      key: AssetInventorySubTab.Income,
      label: t("clients.client.debtSettlement.assetInventory.income"),
      to: `${basePaths.assetInventory}/${AssetInventorySubTab.Income}`,
    },
    {
      key: AssetInventorySubTab.Expenses,
      label: t("clients.client.debtSettlement.assetInventory.expenses"),
      to: `${basePaths.assetInventory}/${AssetInventorySubTab.Expenses}`,
    },
    {
      key: AssetInventorySubTab.OwnClaims,
      label: t("clients.client.debtSettlement.assetInventory.ownClaims"),
      to: `${basePaths.assetInventory}/${AssetInventorySubTab.OwnClaims}`,
    },
    {
      key: AssetInventorySubTab.Assets,
      label: t("clients.client.debtSettlement.assetInventory.assets"),
      to: `${basePaths.assetInventory}/${AssetInventorySubTab.Assets}`,
    },
    {
      key: AssetInventorySubTab.OtherPropertyRights,
      label: t("clients.client.debtSettlement.assetInventory.otherPropertyRights"),
      to: `${basePaths.assetInventory}/${AssetInventorySubTab.OtherPropertyRights}`,
    },
    {
      key: AssetInventorySubTab.Donations,
      label: t("clients.client.debtSettlement.assetInventory.donations"),
      to: `${basePaths.assetInventory}/${AssetInventorySubTab.Donations}`,
    },
    ...(hasDocumentsOverview
      ? [
          {
            key: AssetInventorySubTab.Documents,
            label: t("clients.client.debtSettlement.assetInventory.documentsOverview.title"),
            to: `${basePaths.assetInventory}/${AssetInventorySubTab.Documents}`,
          },
        ]
      : []),
  ];

  return <TabSideNavigation items={items.map(withWarning)} activeKey={activeSubTab} />;
}
